import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ShoppingCart, Trash2, CreditCard, Gift, Tag } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import NoticeBanner from '@/components/NoticeBanner';
import { trackPurchase } from '@/utils/analytics';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function Checkout({ user }) {
  const [cartItems, setCartItems] = useState([]);
  const [paymentMethod, setPaymentMethod] = useState('stripe');
  const [shippingCost, setShippingCost] = useState(0);
  const [shippingInfo, setShippingInfo] = useState(null);
  const [cashback, setCashback] = useState({ balance: 0, expires_at: null });
  const [useCashback, setUseCashback] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [couponCode, setCouponCode] = useState('');
  const [couponDiscount, setCouponDiscount] = useState(0);
  const [couponApplied, setCouponApplied] = useState(null);
  const [validatingCoupon, setValidatingCoupon] = useState(false);
  const [loyalty, setLoyalty] = useState(null);
  const [productCatalog, setProductCatalog] = useState([]);
  const [discountChoice, setDiscountChoice] = useState('none'); // 'vip' | 'cashback' | 'coupon' | 'none'
  // Shipping method: 'home' (Correos Premium 24-48h a domicilio) or
  // 'office_pickup' (Correos Premium 24-48h · recogida en oficina).
  const [shippingMethod, setShippingMethod] = useState('home');
  const [pickupOfficeAddress, setPickupOfficeAddress] = useState('');
  const [pickupOfficeAuto, setPickupOfficeAuto] = useState(false);
  const [formData, setFormData] = useState(() => {
    // Best-effort split of the stored user.name into first+last so that logged-in
    // users don't have to retype their data.
    const fullName = (user?.name || '').trim();
    const spaceIdx = fullName.indexOf(' ');
    const first = spaceIdx === -1 ? fullName : fullName.slice(0, spaceIdx);
    const last = spaceIdx === -1 ? '' : fullName.slice(spaceIdx + 1);
    return {
      first_name: first,
      last_name: last,
      email: user?.email || '',
      phone: user?.phone || '',
      address: user?.address || '',
      city: user?.city || '',
      province: user?.province || '',
      postal_code: user?.postal_code || '',
      country: user?.country || 'España'
    };
  });
  // Postal-code lookup state (Spain): when a 5-digit CP is typed, fetch
  // candidate city/province entries and offer a dropdown if there's more than one.
  const [cpLookupOptions, setCpLookupOptions] = useState([]);
  const [cpLookupLoading, setCpLookupLoading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    fetchCart();
    fetchProductCatalog();
    if (user) {
      fetchCashback();
      fetchLoyalty();
    }
  }, [user]);

  useEffect(() => {
    calculateShipping();
  }, [cartItems, formData.country, formData.postal_code]);

  // Auto-lookup city/province from a 5-digit Spanish postal code (debounced).
  useEffect(() => {
    const cp = (formData.postal_code || '').trim();
    if (cp.length !== 5 || !/^\d{5}$/.test(cp)) {
      setCpLookupOptions([]);
      return;
    }
    const ctrl = new AbortController();
    const t = setTimeout(async () => {
      setCpLookupLoading(true);
      try {
        const r = await fetch(`${BACKEND_URL}/api/postal-lookup/${cp}`, { signal: ctrl.signal });
        const data = await r.json();
        const matches = data.matches || [];
        setCpLookupOptions(matches);
        // If exactly 1 match (most common), autofill silently.
        // If user already typed a city manually, don't overwrite it.
        if (matches.length === 1 && !formData.city) {
          setFormData((prev) => ({
            ...prev,
            city: matches[0].city,
            province: prev.province || matches[0].province,
          }));
        }
      } catch (e) {
        // network error — fail silently
      } finally {
        setCpLookupLoading(false);
      }
    }, 350);
    return () => { ctrl.abort(); clearTimeout(t); };
  }, [formData.postal_code]);

  const fetchCart = async () => {
    const sessionId = localStorage.getItem('sessionId');
    const response = await fetch(`${BACKEND_URL}/api/cart/${sessionId}`);
    const data = await response.json();
    setCartItems(data.items || []);
  };

  const fetchProductCatalog = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/products`);
      const data = await response.json();
      setProductCatalog(data || []);
    } catch (e) {
      setProductCatalog([]);
    }
  };

  const fetchLoyalty = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;
      const response = await fetch(`${BACKEND_URL}/api/user/loyalty`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        const data = await response.json();
        setLoyalty(data);
      }
    } catch (e) {
      console.error('Error fetching loyalty:', e);
    }
  };

  const fetchCashback = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;

      const response = await fetch(`${BACKEND_URL}/api/user/cashback`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      setCashback(data);
    } catch (error) {
      console.error('Error fetching cashback:', error);
    }
  };

  const calculateShipping = async () => {
    if (cartItems.length === 0) return;
    
    const subtotal = cartItems.reduce((sum, item) => sum + (item.price * item.quantity), 0);
    const response = await fetch(`${BACKEND_URL}/api/shipping/calculate`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ subtotal, country: formData.country, postal_code: formData.postal_code })
    });
    const data = await response.json();
    setShippingCost(data.shipping_cost);
    setShippingInfo({
      zone_label: data.zone_label,
      tier_label: data.tier_label,
      free_from: data.free_from,
      next_tier: data.next_tier
    });
  };

  const removeItem = async (productId) => {
    const sessionId = localStorage.getItem('sessionId');
    await fetch(`${BACKEND_URL}/api/cart/${sessionId}/${productId}`, { method: 'DELETE' });
    fetchCart();
  };

  const applyCoupon = async () => {
    if (!couponCode.trim()) return;
    setValidatingCoupon(true);
    try {
      const currentSubtotal = cartItems.reduce((sum, item) => sum + (item.price * item.quantity), 0);
      const response = await fetch(`${BACKEND_URL}/api/coupons/validate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          code: couponCode,
          subtotal: currentSubtotal,
          user_email: formData.email,
          items: cartItems.map((it) => ({
            product_id: it.product_id,
            price: it.price,
            quantity: it.quantity,
          }))
        })
      });
      const data = await response.json();
      if (data.valid) {
        setCouponDiscount(data.discount_amount);
        setCouponApplied(data);
        toast.success(`Cupon "${data.code}" aplicado: -${data.discount_amount.toFixed(2)}\u20AC`);
      } else {
        setCouponDiscount(0);
        setCouponApplied(null);
        toast.error(data.error || 'Cupon no valido');
      }
    } catch (error) {
      toast.error('Error al validar cupon');
    } finally {
      setValidatingCoupon(false);
    }
  };

  const removeCoupon = () => {
    setCouponCode('');
    setCouponDiscount(0);
    setCouponApplied(null);
    toast.success('Cupon eliminado');
  };

  const subtotal = cartItems.reduce((sum, item) => sum + (item.price * item.quantity), 0);

  // Compute VIP preview based on tier + eligible Arthur Fitz non-custom items
  const tierDiscountRate = loyalty?.discount_rate || 0;
  const vipEligibleTotal = cartItems.reduce((sum, item) => {
    const p = productCatalog.find((x) => x.id === item.product_id);
    if (!p) return sum;
    const name = (p.name || '').toLowerCase();
    const isEligible = p.brand === 'Arthur Fitz' && !name.includes('personalizado');
    return isEligible ? sum + item.price * item.quantity : sum;
  }, 0);
  const vipDiscountPreview = Math.round(vipEligibleTotal * tierDiscountRate * 100) / 100;
  const hasVipOption = loyalty && ['plata', 'oro', 'platino', 'pro'].includes(loyalty.tier) && vipDiscountPreview > 0;
  const hasCashbackOption = !!user && cashback.balance > 0;
  const hasCouponOption = !!couponApplied && couponDiscount > 0;

  const availableDiscounts = [];
  if (hasVipOption) availableDiscounts.push('vip');
  if (hasCashbackOption) availableDiscounts.push('cashback');
  if (hasCouponOption) availableDiscounts.push('coupon');

  // Auto-select: default to 'none' if only one option, use that option.
  useEffect(() => {
    if (availableDiscounts.length === 0) {
      setDiscountChoice('none');
    } else if (availableDiscounts.length === 1) {
      setDiscountChoice(availableDiscounts[0]);
    } else if (!availableDiscounts.includes(discountChoice)) {
      // Pick the most valuable automatically
      setDiscountChoice(availableDiscounts[0]);
    }
  }, [hasVipOption, hasCashbackOption, hasCouponOption]);

  // Keep old useCashback flag in sync with choice for backward-compat
  useEffect(() => {
    setUseCashback(discountChoice === 'cashback');
  }, [discountChoice]);

  const appliedVipDiscount = discountChoice === 'vip' ? vipDiscountPreview : 0;
  const appliedCashback = discountChoice === 'cashback' ? Math.min(cashback.balance, subtotal) : 0;
  const appliedCouponDiscount = discountChoice === 'coupon' ? couponDiscount : 0;
  const cashbackDiscount = appliedCashback; // alias for existing UI
  const total = subtotal + shippingCost - appliedVipDiscount - appliedCashback - appliedCouponDiscount;

  const handleCheckout = async () => {
    // Validar campos requeridos
    if (!formData.first_name || !formData.last_name || !formData.email || !formData.phone || !formData.address || !formData.city) {
      toast.error('Por favor, completa todos los campos requeridos (nombre y apellidos incluidos)');
      return;
    }
    // Validate Spanish postal code (5 digits, province prefix 01-52)
    const cp = (formData.postal_code || '').trim();
    const isSpain = ['españa', 'espana', 'spain', 'es'].includes((formData.country || '').toLowerCase());
    if (!cp) {
      toast.error('El código postal es obligatorio');
      return;
    }
    if (isSpain) {
      const prov = parseInt(cp.slice(0, 2), 10);
      if (!/^\d{5}$/.test(cp) || !Number.isFinite(prov) || prov < 1 || prov > 52) {
        toast.error('Código postal español no válido. Debe tener 5 dígitos (ej: 28013, 35001, 07001)');
        return;
      }
    }
    // Validate pickup-in-office option: needs either an explicit address or the auto-selection flag
    if (shippingMethod === 'office_pickup' && !pickupOfficeAuto && !pickupOfficeAddress.trim()) {
      toast.error('Indica la dirección de la oficina de Correos o marca "elegir la más cercana"');
      return;
    }

    if (processing) return; // Prevent double submission
    setProcessing(true);

    // Combine first + last name into the single `name` field the backend expects.
    // We also keep the parts separately so that future backend changes can use
    // them without a frontend contract change.
    const fullName = `${formData.first_name.trim()} ${formData.last_name.trim()}`.trim();

    try {
      const token = localStorage.getItem('token');
      // If we have a token but the parent `user` prop hasn't loaded yet
      // (race condition on refresh / slow /auth/me), re-verify against the
      // backend BEFORE we mark the order as guest. This is critical so that
      // Google-OAuth customers earn cashback + loyalty on every purchase.
      let effectiveUser = user;
      if (token && !effectiveUser) {
        try {
          const res = await fetch(`${BACKEND_URL}/api/auth/me`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          if (res.ok) {
            effectiveUser = await res.json();
          }
        } catch (_) { /* fall through — will be treated as guest */ }
      }
      const isGuest = !effectiveUser || !token;

      // Create order with new endpoint
      const checkoutData = {
        items: cartItems,
        user_data: {
          name: fullName,
          first_name: formData.first_name.trim(),
          last_name: formData.last_name.trim(),
          email: formData.email,
          phone: formData.phone,
          address: formData.address,
          city: formData.city,
          province: formData.province,
          postal_code: formData.postal_code,
          country: formData.country,
          subtotal: subtotal,
          shipping_method: shippingMethod,
          pickup_office_address: shippingMethod === 'office_pickup' && !pickupOfficeAuto ? pickupOfficeAddress.trim() : null,
          pickup_office_auto: shippingMethod === 'office_pickup' && pickupOfficeAuto
        },
        payment_method: paymentMethod,
        use_cashback: discountChoice === 'cashback' && !isGuest,
        is_guest: isGuest,
        coupon_code: discountChoice === 'coupon' && couponApplied ? couponApplied.code : null,
        discount_choice: availableDiscounts.length > 0 ? discountChoice : 'none'
      };

      const headers = { 'Content-Type': 'application/json' };
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      const orderResponse = await fetch(`${BACKEND_URL}/api/orders/create`, {
        method: 'POST',
        headers: headers,
        body: JSON.stringify(checkoutData)
      });

      if (!orderResponse.ok) {
        const error = await orderResponse.json();
        toast.error(error.detail || 'Error al crear el pedido');
        setProcessing(false);
        return;
      }

      const orderResult = await orderResponse.json();

      // Show discounts earned
      if (orderResult.cashback_earned > 0) {
        toast.success(`¡Has ganado ${orderResult.cashback_earned.toFixed(2)}€ en cashback!`);
      }
      if (orderResult.first_order_discount > 0) {
        toast.success(`¡Descuento primer pedido aplicado: ${orderResult.first_order_discount.toFixed(2)}€!`);
      }

      if (paymentMethod === 'stripe') {
        console.log('Creando sesión de Stripe...');
        // Stripe orders live as a DRAFT in pending_orders until the webhook
        // confirms payment — backend returns `draft_id` instead of `order_id`.
        const stripeRefId = orderResult.draft_id || orderResult.order_id;
        const paymentResponse = await fetch(`${BACKEND_URL}/api/payments/stripe/create-session`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            order_id: stripeRefId,
            amount: orderResult.total,
            origin_url: window.location.origin,
            user_email: formData.email
          })
        });
        
        console.log('Stripe response status:', paymentResponse.status);
        
        if (!paymentResponse.ok) {
          const errorData = await paymentResponse.json();
          console.error('Stripe error:', errorData);
          toast.error('Error al crear sesión de pago');
          setProcessing(false);
          return;
        }
        
        const stripeData = await paymentResponse.json();
        console.log('Stripe data:', stripeData);
        
        if (!stripeData.url) {
          toast.error('No se recibió URL de pago');
          setProcessing(false);
          return;
        }
        
        // NO vaciar carrito aquí - se vaciará después del pago exitoso
        console.log('Redirigiendo a:', stripeData.url);
        
        // Redirect to Stripe
        window.location.href = stripeData.url;
      } else if (paymentMethod === 'bizum') {
        toast.success('Pedido creado. Revisa tu email para instrucciones de pago Bizum.');
        // Google Ads purchase conversion (Bizum orders are counted when placed;
        // dedup key = order_id)
        trackPurchase({ transactionId: orderResult.order_id, value: orderResult.total, currency: 'EUR' });

        // Clear cart
        const sessionId = localStorage.getItem('sessionId');
        await fetch(`${BACKEND_URL}/api/cart/${sessionId}`, { method: 'DELETE' });

        setProcessing(false);
        navigate('/payment-success');
      } else if (paymentMethod === 'transferencia') {
        toast.success('Pedido creado. Revisa tu email para los datos de transferencia bancaria.');
        trackPurchase({ transactionId: orderResult.order_id, value: orderResult.total, currency: 'EUR' });

        const sessionId = localStorage.getItem('sessionId');
        await fetch(`${BACKEND_URL}/api/cart/${sessionId}`, { method: 'DELETE' });

        setProcessing(false);
        navigate('/payment-success');
      }
    } catch (error) {
      console.error('Checkout error:', error);
      toast.error('Error al procesar el pedido. Inténtalo de nuevo.');
      setProcessing(false);
    }
  };

  if (cartItems.length === 0) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <ShoppingCart className="w-24 h-24 mx-auto text-gray-300 mb-4" />
          <h2 className="text-3xl font-bold mb-4" style={{ fontFamily: 'Playfair Display' }}>Carrito vacío</h2>
          <Link to="/productos"><Button className="bg-black text-white">IR DE COMPRAS</Button></Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white py-12 px-6">
      <div className="container mx-auto max-w-6xl">
        <Link to="/productos" className="text-sm mb-6 inline-block hover:underline">← Volver</Link>
        <h1 className="text-4xl font-bold mb-8" style={{ fontFamily: 'Playfair Display' }}>Finalizar Compra</h1>

        <div className="grid md:grid-cols-2 gap-8">
          {/* Order Summary */}
          <div className="border-2 border-black p-6">
            <h2 className="text-2xl font-bold mb-6" style={{ fontFamily: 'Playfair Display' }}>Resumen del Pedido</h2>
            <div className="space-y-4 mb-6">
              {cartItems.map((item) => (
                <div key={item.product_id} className="flex justify-between items-start border-b pb-4">
                  <div className="flex-1">
                    <h3 className="font-medium">{item.product_name}</h3>
                    {item.variant && (
                      <p className="text-sm text-gray-600">
                        {item.variant.size && `Tamaño: ${item.variant.size}`}
                        {item.variant.color && `, Color: ${item.variant.color}`}
                        {item.variant.density && `, Densidad: ${item.variant.density}`}
                      </p>
                    )}
                    <p className="text-sm text-gray-600">Cantidad: {item.quantity}</p>
                  </div>
                  <div className="text-right">
                    <p className="font-bold">{(item.price * item.quantity).toFixed(2)}€</p>
                    <button onClick={() => removeItem(item.product_id)} className="text-red-600 hover:underline text-sm mt-2">
                      <Trash2 className="w-4 h-4 inline" /> Eliminar
                    </button>
                  </div>
                </div>
              ))}
            </div>

            <div className="border-t-2 border-black pt-4 space-y-2">
              <div className="flex justify-between text-lg">
                <span>Subtotal:</span>
                <span>{subtotal.toFixed(2)}€</span>
              </div>
              <div className="flex justify-between text-lg">
                <span>Envio:</span>
                <span>{shippingCost === 0 ? 'GRATIS' : `${shippingCost.toFixed(2)}\u20AC`}</span>
              </div>
              {shippingInfo && (
                <div className="text-xs text-gray-500 -mt-1 space-y-0.5">
                  <p>Zona: <span className="font-medium text-gray-700">{shippingInfo.zone_label}</span></p>
                  {shippingInfo.free_from && shippingCost > 0 && (
                    <p className="text-green-600">
                      Envio gratis a partir de {shippingInfo.free_from.toFixed(2)}&euro;
                      {(shippingInfo.free_from - subtotal) > 0 && (
                        <span> (te faltan {(shippingInfo.free_from - subtotal).toFixed(2)}&euro;)</span>
                      )}
                    </p>
                  )}
                  {shippingInfo.next_tier && shippingCost > 0 && !shippingInfo.free_from && (
                    <p className="text-blue-600">
                      Añade {(shippingInfo.next_tier.min_subtotal - subtotal).toFixed(2)}&euro; mas para envio a {shippingInfo.next_tier.shipping_cost.toFixed(2)}&euro;
                    </p>
                  )}
                </div>
              )}
              {appliedVipDiscount > 0 && (
                <div className="flex justify-between text-lg text-black font-semibold" data-testid="vip-discount-row">
                  <span>Descuento ArtFitz Club ({Math.round(tierDiscountRate * 100)}%):</span>
                  <span>-{appliedVipDiscount.toFixed(2)}€</span>
                </div>
              )}
              {cashbackDiscount > 0 && (
                <div className="flex justify-between text-lg text-green-600">
                  <span>Descuento Cashback:</span>
                  <span>-{cashbackDiscount.toFixed(2)}&euro;</span>
                </div>
              )}
              {appliedCouponDiscount > 0 && (
                <div className="flex justify-between text-lg text-green-600">
                  <span>Cupon ({couponApplied?.code}):</span>
                  <span>-{appliedCouponDiscount.toFixed(2)}&euro;</span>
                </div>
              )}
              <div className="flex justify-between text-2xl font-bold pt-2 border-t">
                <span>Total:</span>
                <span>{total.toFixed(2)}€</span>
              </div>
            </div>

            {/* Coupon Code */}
            <div className="mt-4 pt-4 border-t">
              <div className="flex items-center gap-2 mb-2">
                <Tag className="w-4 h-4" />
                <span className="text-sm font-medium">Cupon de descuento</span>
              </div>
              {couponApplied ? (
                <div className="flex items-center justify-between bg-green-50 border border-green-200 p-3 rounded">
                  <div>
                    <span className="font-mono font-bold text-green-800">{couponApplied.code}</span>
                    <span className="text-sm text-green-700 ml-2">
                      -{couponApplied.discount_type === 'percentage'
                        ? `${couponApplied.discount_value}%`
                        : `${couponApplied.discount_value.toFixed(2)}\u20AC`}
                    </span>
                  </div>
                  <button
                    data-testid="remove-coupon-btn"
                    onClick={removeCoupon}
                    className="text-red-600 text-sm hover:underline"
                  >
                    Eliminar
                  </button>
                </div>
              ) : (
                <div className="flex gap-2">
                  <Input
                    data-testid="coupon-input"
                    value={couponCode}
                    onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                    placeholder="Introduce tu codigo"
                    className="font-mono uppercase flex-1"
                    onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); applyCoupon(); } }}
                  />
                  <Button
                    data-testid="apply-coupon-btn"
                    onClick={applyCoupon}
                    variant="outline"
                    className="border-2 border-black"
                    disabled={validatingCoupon || !couponCode.trim()}
                  >
                    {validatingCoupon ? '...' : 'Aplicar'}
                  </Button>
                </div>
              )}
            </div>
          </div>

          {/* Checkout Form */}
          <div className="space-y-6">
            {!user && (
              <div className="bg-yellow-50 border-2 border-yellow-400 p-4 rounded">
                <p className="text-sm font-bold text-yellow-900 mb-2">⚠️ Importante</p>
                <p className="text-sm text-yellow-800 mb-2">
                  <strong>Compra como invitado</strong> o <Link to="/" className="text-black underline font-bold">inicia sesión</Link>
                </p>
                <div className="mt-3 bg-white p-3 rounded border border-yellow-300">
                  <p className="text-sm font-medium mb-2">Al crear una cuenta recibes:</p>
                  <ul className="text-xs space-y-1 ml-4">
                    <li>✓ <strong>3% cashback</strong> en productos Arthur Fitz</li>
                    <li>✓ Usa el código <strong>PRIMERPEDIDO</strong> para un 5% en pedidos +80€</li>
                    <li>✓ Acceso al <strong>programa VIP</strong> (hasta 20% dto.)</li>
                    <li>✓ Historial de pedidos</li>
                  </ul>
                </div>
                <p className="text-xs text-yellow-700 mt-2">
                  * El cashback y programa VIP solo están disponibles para clientes registrados
                </p>
              </div>
            )}

            {/* Discount Choice - VIP / Cashback / Coupon (mutually exclusive) */}
            {availableDiscounts.length > 0 && (
              <div className="border-2 border-black p-4 rounded space-y-3" data-testid="discount-choice-section">
                <div className="flex items-center gap-2 mb-1">
                  <Gift className="w-5 h-5" />
                  <h3 className="font-bold">Elige tu descuento</h3>
                </div>
                <p className="text-xs text-gray-600">
                  Solo puedes aplicar uno por pedido.
                </p>

                {hasVipOption && (
                  <label className="flex items-start gap-3 cursor-pointer border p-3 rounded hover:bg-gray-50" data-testid="discount-choice-vip">
                    <input
                      type="radio"
                      name="discount-choice"
                      value="vip"
                      checked={discountChoice === 'vip'}
                      onChange={(e) => setDiscountChoice(e.target.value)}
                      className="mt-1"
                    />
                    <div className="flex-1">
                      <div className="flex items-center justify-between">
                        <span className="font-semibold">
                          ArtFitz Club {loyalty.tier.charAt(0).toUpperCase() + loyalty.tier.slice(1)}
                        </span>
                        <span className="font-bold text-black">
                          -{vipDiscountPreview.toFixed(2)}€
                        </span>
                      </div>
                      <p className="text-xs text-gray-600 mt-1">
                        {Math.round(tierDiscountRate * 100)}% en productos Arthur Fitz elegibles
                      </p>
                    </div>
                  </label>
                )}

                {hasCashbackOption && (
                  <label className="flex items-start gap-3 cursor-pointer border p-3 rounded hover:bg-gray-50" data-testid="discount-choice-cashback">
                    <input
                      type="radio"
                      name="discount-choice"
                      value="cashback"
                      checked={discountChoice === 'cashback'}
                      onChange={(e) => setDiscountChoice(e.target.value)}
                      className="mt-1"
                    />
                    <div className="flex-1">
                      <div className="flex items-center justify-between">
                        <span className="font-semibold">Usar mi cashback</span>
                        <span className="font-bold text-green-700">
                          -{Math.min(cashback.balance, subtotal).toFixed(2)}€
                        </span>
                      </div>
                      <p className="text-xs text-gray-600 mt-1">
                        Saldo disponible: {cashback.balance.toFixed(2)}€
                      </p>
                    </div>
                  </label>
                )}

                {hasCouponOption && (
                  <label className="flex items-start gap-3 cursor-pointer border p-3 rounded hover:bg-gray-50" data-testid="discount-choice-coupon">
                    <input
                      type="radio"
                      name="discount-choice"
                      value="coupon"
                      checked={discountChoice === 'coupon'}
                      onChange={(e) => setDiscountChoice(e.target.value)}
                      className="mt-1"
                    />
                    <div className="flex-1">
                      <div className="flex items-center justify-between">
                        <span className="font-semibold">Cupón {couponApplied.code}</span>
                        <span className="font-bold text-black">
                          -{couponDiscount.toFixed(2)}€
                        </span>
                      </div>
                    </div>
                  </label>
                )}

                {availableDiscounts.length > 1 && (
                  <label className="flex items-center gap-3 cursor-pointer text-sm text-gray-500" data-testid="discount-choice-none">
                    <input
                      type="radio"
                      name="discount-choice"
                      value="none"
                      checked={discountChoice === 'none'}
                      onChange={(e) => setDiscountChoice(e.target.value)}
                    />
                    Sin descuento (ganar cashback)
                  </label>
                )}
              </div>
            )}

            {/* Legacy Cashback Section - hidden when we use the multi-option picker above */}
            {user && cashback.balance > 0 && availableDiscounts.length === 0 && (
              <div className="bg-green-50 border-2 border-green-200 p-4 rounded">
                <div className="flex items-center gap-2 mb-2">
                  <Gift className="w-5 h-5 text-green-600" />
                  <h3 className="font-bold text-green-800">Cashback Disponible</h3>
                </div>
                <p className="text-sm text-green-700 mb-3">
                  Tienes <strong>{cashback.balance.toFixed(2)}€</strong> disponibles
                  {cashback.expires_at && (
                    <span className="block text-xs mt-1">
                      Caduca: {new Date(cashback.expires_at).toLocaleDateString('es-ES')}
                    </span>
                  )}
                </p>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={useCashback}
                    onChange={(e) => setUseCashback(e.target.checked)}
                    className="w-4 h-4"
                  />
                  <span className="text-sm font-medium">Usar cashback en este pedido</span>
                </label>
              </div>
            )}

            <div className="border-2 border-black p-6">
              <h2 className="text-2xl font-bold mb-6" style={{ fontFamily: 'Playfair Display' }}>Datos de Envío</h2>
              <div className="space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <Label>Nombre *</Label>
                    <Input
                      value={formData.first_name}
                      onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                      placeholder="Juan"
                      autoComplete="given-name"
                      required
                      data-testid="checkout-first-name-input"
                    />
                  </div>
                  <div>
                    <Label>Apellidos *</Label>
                    <Input
                      value={formData.last_name}
                      onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                      placeholder="Pérez García"
                      autoComplete="family-name"
                      required
                      data-testid="checkout-last-name-input"
                    />
                  </div>
                </div>
                <div>
                  <Label>Email *</Label>
                  <Input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    onBlur={(e) => {
                      if (e.target.value) {
                        const sid = localStorage.getItem('sessionId');
                        if (sid) fetch(`${BACKEND_URL}/api/cart/${sid}/email`, { method: 'PUT', headers: {'Content-Type': 'application/json'}, body: JSON.stringify({email: e.target.value}) }).catch(() => {});
                      }
                    }}
                    placeholder="tu@email.com"
                    inputMode="email"
                    autoComplete="email"
                    required
                  />
                </div>
                <div>
                  <Label>Teléfono *</Label>
                  <Input
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    placeholder="+34 600 000 000"
                    inputMode="tel"
                    autoComplete="tel"
                    required
                  />
                </div>
                <div>
                  <Label>Dirección *</Label>
                  <Input
                    value={formData.address}
                    onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                    placeholder="Calle Principal 123"
                    autoComplete="street-address"
                    required
                  />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <Label>Código Postal *</Label>
                    <Input
                      value={formData.postal_code}
                      onChange={(e) => {
                        // Only accept digits, max 5 chars — Spanish CP format
                        const digits = e.target.value.replace(/\D/g, '').slice(0, 5);
                        setFormData({ ...formData, postal_code: digits });
                      }}
                      inputMode="numeric"
                      pattern="[0-9]{5}"
                      placeholder="28001"
                      maxLength={5}
                      autoComplete="postal-code"
                      required
                      data-testid="checkout-postal-code"
                    />
                    {cpLookupLoading && <p className="text-xs text-gray-500 mt-1">Buscando ciudad...</p>}
                  </div>
                  <div>
                    <Label>Ciudad / Población *</Label>
                    {cpLookupOptions.length > 1 ? (
                      <Select
                        value={formData.city}
                        onValueChange={(val) => {
                          const match = cpLookupOptions.find((o) => o.city === val);
                          setFormData({
                            ...formData,
                            city: val,
                            province: match?.province || formData.province,
                          });
                        }}
                      >
                        <SelectTrigger data-testid="checkout-city-select">
                          <SelectValue placeholder="Selecciona tu población" />
                        </SelectTrigger>
                        <SelectContent>
                          {cpLookupOptions.map((o) => (
                            <SelectItem key={o.city} value={o.city}>{o.city}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    ) : (
                      <Input
                        value={formData.city}
                        onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                        placeholder="Madrid"
                        autoComplete="address-level2"
                        required
                      />
                    )}
                  </div>
                </div>
                <div>
                  <Label>Provincia</Label>
                  <Input
                    value={formData.province}
                    onChange={(e) => setFormData({ ...formData, province: e.target.value })}
                    placeholder="Madrid"
                    autoComplete="address-level1"
                    data-testid="checkout-province"
                  />
                </div>
                <div>
                  <Label>País *</Label>
                  <Select value={formData.country} onValueChange={(val) => setFormData({ ...formData, country: val })}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="max-h-80">
                      <SelectItem value="España">España</SelectItem>
                      <SelectItem value="Islas Canarias">Islas Canarias</SelectItem>
                      <SelectItem value="Islas Baleares">Islas Baleares</SelectItem>
                      {/* Europa (19,95€ UPS/DHL/Correos Premium) */}
                      <SelectItem value="Portugal">Portugal</SelectItem>
                      <SelectItem value="Francia">Francia</SelectItem>
                      <SelectItem value="Alemania">Alemania</SelectItem>
                      <SelectItem value="Italia">Italia</SelectItem>
                      <SelectItem value="Reino Unido">Reino Unido</SelectItem>
                      <SelectItem value="Paises Bajos">Países Bajos</SelectItem>
                      <SelectItem value="Belgica">Bélgica</SelectItem>
                      <SelectItem value="Austria">Austria</SelectItem>
                      <SelectItem value="Suiza">Suiza</SelectItem>
                      <SelectItem value="Irlanda">Irlanda</SelectItem>
                      <SelectItem value="Dinamarca">Dinamarca</SelectItem>
                      <SelectItem value="Suecia">Suecia</SelectItem>
                      <SelectItem value="Noruega">Noruega</SelectItem>
                      <SelectItem value="Finlandia">Finlandia</SelectItem>
                      <SelectItem value="Polonia">Polonia</SelectItem>
                      <SelectItem value="Republica Checa">República Checa</SelectItem>
                      <SelectItem value="Grecia">Grecia</SelectItem>
                      <SelectItem value="Rumania">Rumanía</SelectItem>
                      <SelectItem value="Hungria">Hungría</SelectItem>
                      <SelectItem value="Bulgaria">Bulgaria</SelectItem>
                      <SelectItem value="Croacia">Croacia</SelectItem>
                      <SelectItem value="Eslovenia">Eslovenia</SelectItem>
                      <SelectItem value="Eslovaquia">Eslovaquia</SelectItem>
                      <SelectItem value="Estonia">Estonia</SelectItem>
                      <SelectItem value="Letonia">Letonia</SelectItem>
                      <SelectItem value="Lituania">Lituania</SelectItem>
                      <SelectItem value="Luxemburgo">Luxemburgo</SelectItem>
                      <SelectItem value="Malta">Malta</SelectItem>
                      <SelectItem value="Chipre">Chipre</SelectItem>
                      <SelectItem value="Islandia">Islandia</SelectItem>
                      <SelectItem value="Andorra">Andorra</SelectItem>
                      <SelectItem value="Monaco">Mónaco</SelectItem>
                      {/* Resto del Mundo (29,90€ DHL/UPS) */}
                      <SelectItem value="Estados Unidos">Estados Unidos</SelectItem>
                      <SelectItem value="Canada">Canadá</SelectItem>
                      <SelectItem value="Mexico">México</SelectItem>
                      <SelectItem value="Argentina">Argentina</SelectItem>
                      <SelectItem value="Brasil">Brasil</SelectItem>
                      <SelectItem value="Chile">Chile</SelectItem>
                      <SelectItem value="Colombia">Colombia</SelectItem>
                      <SelectItem value="Peru">Perú</SelectItem>
                      <SelectItem value="Uruguay">Uruguay</SelectItem>
                      <SelectItem value="Venezuela">Venezuela</SelectItem>
                      <SelectItem value="Ecuador">Ecuador</SelectItem>
                      <SelectItem value="Republica Dominicana">República Dominicana</SelectItem>
                      <SelectItem value="Panama">Panamá</SelectItem>
                      <SelectItem value="Costa Rica">Costa Rica</SelectItem>
                      <SelectItem value="Marruecos">Marruecos</SelectItem>
                      <SelectItem value="Emiratos Arabes Unidos">Emiratos Árabes Unidos</SelectItem>
                      <SelectItem value="Arabia Saudi">Arabia Saudí</SelectItem>
                      <SelectItem value="Israel">Israel</SelectItem>
                      <SelectItem value="Japon">Japón</SelectItem>
                      <SelectItem value="China">China</SelectItem>
                      <SelectItem value="Corea del Sur">Corea del Sur</SelectItem>
                      <SelectItem value="India">India</SelectItem>
                      <SelectItem value="Australia">Australia</SelectItem>
                      <SelectItem value="Nueva Zelanda">Nueva Zelanda</SelectItem>
                      <SelectItem value="Sudafrica">Sudáfrica</SelectItem>
                      <SelectItem value="Otro">Otro país (Resto del Mundo)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>

            <div className="border-2 border-black p-6" data-testid="shipping-method-section">
              <h2 className="text-2xl font-bold mb-2" style={{ fontFamily: 'Playfair Display' }}>Modalidad de envío</h2>
              <p className="text-sm text-gray-600 mb-4">Todos los pedidos se envían por <strong>Correos Premium</strong> en 24-48h con envío 100% discreto.</p>
              <div className="space-y-3">
                <label className={`flex items-start gap-3 p-3 border-2 rounded cursor-pointer ${shippingMethod === 'home' ? 'border-black bg-gray-50' : 'border-gray-300 hover:border-black'}`} data-testid="shipping-home-option">
                  <input
                    type="radio"
                    name="shipping_method"
                    value="home"
                    checked={shippingMethod === 'home'}
                    onChange={() => setShippingMethod('home')}
                    className="mt-1"
                  />
                  <div>
                    <p className="font-semibold">Correos Premium 24-48h · Entrega a domicilio</p>
                    <p className="text-sm text-gray-600">Entrega en la dirección que has indicado arriba.</p>
                  </div>
                </label>

                <label className={`flex items-start gap-3 p-3 border-2 rounded cursor-pointer ${shippingMethod === 'office_pickup' ? 'border-black bg-gray-50' : 'border-gray-300 hover:border-black'}`} data-testid="shipping-office-option">
                  <input
                    type="radio"
                    name="shipping_method"
                    value="office_pickup"
                    checked={shippingMethod === 'office_pickup'}
                    onChange={() => setShippingMethod('office_pickup')}
                    className="mt-1"
                  />
                  <div className="flex-1">
                    <p className="font-semibold">Correos Premium 24-48h · Recogida en oficina</p>
                    <p className="text-sm text-gray-600">Recibirás un aviso cuando el paquete esté disponible para recoger.</p>

                    {shippingMethod === 'office_pickup' && (
                      <div className="mt-3 space-y-3">
                        <label className="flex items-start gap-2 text-sm">
                          <input
                            type="checkbox"
                            checked={pickupOfficeAuto}
                            onChange={(e) => setPickupOfficeAuto(e.target.checked)}
                            className="mt-0.5"
                            data-testid="pickup-office-auto-checkbox"
                          />
                          <span>Elegid vosotros la oficina de Correos más cercana a mi domicilio.</span>
                        </label>

                        {!pickupOfficeAuto && (
                          <div>
                            <Label className="text-sm">Dirección de la oficina de Correos *</Label>
                            <textarea
                              value={pickupOfficeAddress}
                              onChange={(e) => setPickupOfficeAddress(e.target.value)}
                              placeholder="Ej: Oficina Correos Madrid Centro - Calle Alcalá 15, 28014 Madrid"
                              className="w-full mt-1 border-2 border-gray-300 p-2 rounded text-sm focus:border-black focus:outline-none"
                              rows="2"
                              data-testid="pickup-office-address-input"
                            />
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </label>
              </div>
            </div>

            <div className="border-2 border-black p-6">
              <h2 className="text-2xl font-bold mb-6" style={{ fontFamily: 'Playfair Display' }}>Método de Pago</h2>
              <div className="space-y-3">
                <label className="flex items-center gap-3 p-3 border-2 border-gray-300 rounded cursor-pointer hover:border-black">
                  <input
                    type="radio"
                    name="payment"
                    value="stripe"
                    checked={paymentMethod === 'stripe'}
                    onChange={() => setPaymentMethod('stripe')}
                  />
                  <CreditCard className="w-5 h-5" />
                  <span className="font-medium">Tarjeta (Stripe)</span>
                </label>
                <div>
                  <label className="flex items-center gap-3 p-3 border-2 border-gray-300 rounded cursor-pointer hover:border-black" data-testid="payment-bizum">
                    <input
                      type="radio"
                      name="payment"
                      value="bizum"
                      checked={paymentMethod === 'bizum'}
                      onChange={() => setPaymentMethod('bizum')}
                    />
                    <span className="font-medium">Bizum</span>
                  </label>
                  {paymentMethod === 'bizum' && (
                    <div className="mt-2 p-4 bg-gray-50 border-2 border-gray-200 rounded text-sm" data-testid="bizum-details">
                      <p className="font-bold mb-2">Datos para el pago por Bizum:</p>
                      <div className="space-y-1">
                        <p><span className="text-gray-600">Teléfono:</span> <strong>649 471 889</strong></p>
                        <p><span className="text-gray-600">Titular:</span> <strong>Juan Miguel PC</strong></p>
                      </div>
                      <p className="text-xs text-gray-500 mt-3">Indica el número de pedido en el concepto. Una vez realizado el pago, envía el comprobante a info@arthurfitz.com</p>
                    </div>
                  )}
                </div>
                <div>
                  <label className="flex items-center gap-3 p-3 border-2 border-gray-300 rounded cursor-pointer hover:border-black" data-testid="payment-transfer">
                    <input
                      type="radio"
                      name="payment"
                      value="transferencia"
                      checked={paymentMethod === 'transferencia'}
                      onChange={() => setPaymentMethod('transferencia')}
                    />
                    <span className="font-medium">Transferencia Bancaria</span>
                  </label>
                  {paymentMethod === 'transferencia' && (
                    <div className="mt-2 p-4 bg-gray-50 border-2 border-gray-200 rounded text-sm" data-testid="transfer-details">
                      <p className="font-bold mb-2">Datos para la transferencia:</p>
                      <div className="space-y-1">
                        <p><span className="text-gray-600">Banco:</span> <strong>CaixaBank</strong></p>
                        <p><span className="text-gray-600">Titular:</span> <strong>Juan Miguel Perez</strong></p>
                        <p><span className="text-gray-600">IBAN:</span> <strong className="tracking-wide">ES11 2100 2225 4602 0019 5464</strong></p>
                      </div>
                      <p className="text-xs text-gray-500 mt-3">Indica el número de pedido en el concepto. Una vez realizada la transferencia, envía el comprobante a info@arthurfitz.com</p>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {!user && (
              <div className="bg-yellow-50 border-2 border-yellow-200 p-4 rounded text-sm">
                <p className="font-medium mb-1">💡 ¿Sabías que?</p>
                <p>Al crear una cuenta ganas <strong>3% de cashback</strong> en productos Arthur Fitz para tu próxima compra.</p>
              </div>
            )}

            <Button
              onClick={handleCheckout}
              className="w-full bg-black text-white text-lg py-6 hover:bg-gray-800"
              disabled={processing}
            >
              {processing ? 'PROCESANDO...' : `REALIZAR PEDIDO - ${total.toFixed(2)}€`}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
